"use client";

import {
  clearBootRecovery,
  getDbBlocked,
  hardResetOfflineStorage,
  markBootRecovery,
  readBootRecovery,
  subscribeDbBlocked,
  useAISync,
  type BootRecoveryReason,
} from "@stockline/web";
import * as Sentry from "@sentry/react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

/**
 * How long the boot splash may sit un-ready before we silently re-kick the
 * SyncEngine once. Covers a cold start whose `start()` bailed early (a
 * generation was invalidated, a transient throw) without ever bothering the
 * user — most genuine slow-but-fine boots resolve well under this.
 */
const SOFT_RETRY_MS = 8_000;

/**
 * How often to re-check when the soft-retry deadline lands while a start is
 * still in flight. We defer (rather than burn the one-shot retry) so a start
 * that later bails without becoming ready still gets its single re-kick.
 */
const SOFT_RETRY_RECHECK_MS = 1_000;

/**
 * How long before we give up waiting silently and reveal the recovery panel
 * (a single Restart action). Past this point the boot is considered stuck:
 * on a healthy connection the critical wave lands in a second or two, so a
 * splash this old means something is wedged (blocked DB, dead network, a
 * critical resource stuck in `error`).
 */
const STALL_MS = 15_000;

/**
 * Shorter fuse when we KNOW the offline DB is blocked (IndexedDB fired
 * `blocked` — almost always a second tab holding an old schema open). There is
 * nothing to wait for in that case: `offlineDb.open()` will never resolve on
 * its own, so surface the escape hatch quickly.
 */
const BLOCKED_STALL_MS = 3_000;

/**
 * A boot-recovery breadcrumb older than this is treated as unrelated to the
 * current boot (a reload from an earlier session, days ago). Only a RECENT
 * recovery drives the escalation ladder / `boot.recovered` reporting.
 */
const RECOVERY_RECENT_MS = 90_000;

/**
 * Reasons that, if they preceded THIS boot and it STILL stalled, mean a plain
 * reload didn't clear the wedge — so the watchdog escalates straight to a local
 * reset instead of asking the user to reload into the same wall again. A prior
 * `reset` / `auto-reset` is deliberately excluded: we already wiped once, so we
 * stop auto-acting and fall back to the manual panel (no reset loop).
 */
const ESCALATE_FROM: ReadonlySet<BootRecoveryReason> = new Set([
  "reload",
  "stale-upgrade",
]);

export interface BootRecoveryState {
  /** Reveal the recovery panel — the boot is considered stuck. */
  stalled: boolean;
  /** IndexedDB reported the DB blocked (multi-tab / mid-deploy upgrade). */
  blocked: boolean;
  /**
   * The watchdog is auto-recovering (a prior restart didn't help, so it is
   * resetting local data without waiting for the user). Drives a distinct
   * "recovering automatically…" panel state.
   */
  autoRecovering: boolean;
  /**
   * The single user-facing recovery action: hard reload the tab. If the boot
   * STILL stalls afterwards, the watchdog escalates to a local reset on its
   * own, so this stays a one-button escape hatch.
   */
  restart: () => void;
}

/**
 * Watchdog for the global boot splash. While the splash is `active` (up and not
 * yet ready) it runs a staged timeline: a silent soft-retry of the SyncEngine,
 * then — if still stuck — it either escalates automatically (when a previous
 * reload already failed to clear the wedge) or flips `stalled` so the boot
 * screen can offer the user a way out. Everything resets the moment the boot
 * becomes ready or the splash goes inactive, so a normal fast boot never sees
 * any of it. All transitions are mirrored to the operational telemetry sink
 * (`boot.*`) and, on a genuine stall, to Sentry — so the previously-silent
 * "stuck forever" failure is observable and alertable.
 */
export function useBootRecovery(active: boolean): BootRecoveryState {
  const { engine } = useAISync();
  const blocked = useSyncExternalStore(
    subscribeDbBlocked,
    getDbBlocked,
    () => false,
  );

  const [stalled, setStalled] = useState(false);
  const [autoRecovering, setAutoRecovering] = useState(false);
  const softRetriedRef = useRef(false);
  // Set the instant a reset starts. Gates the soft-retry loop so it can never
  // re-kick `engine.start()` (reopening the DB) while `hardResetOfflineStorage`
  // is mid-wipe — `engine.stop()` there flips `isStarting` back to false, which
  // would otherwise let a still-pending retry fire during deletion.
  const resettingRef = useRef(false);
  // When the splash first became active. Timers are scheduled off ELAPSED time
  // from this anchor (not from each effect re-run), so a `blocked` event that
  // fires late — which re-runs this effect — can only bring the panel forward,
  // never push it past `STALL_MS`.
  const activeSinceRef = useRef<number | null>(null);
  // Recovery breadcrumb read ONCE at mount: did this boot follow a reload/reset
  // (or a stale-tab auto-reload) from the previous page load? Drives escalation
  // + `boot.recovered`. Captured before any of our own actions overwrite it.
  const priorRecoveryRef = useRef(readBootRecovery());
  // Whether this active cycle ever hit the stall/escalation point — so the
  // completion branch can report a genuine recovery vs. a normal fast boot.
  const stalledThisCycleRef = useRef(false);

  const runReset = useCallback(
    (reason: Extract<BootRecoveryReason, "reset" | "auto-reset">) => {
      // Gate the soft-retry loop BEFORE stopping the engine: `hardResetOfflineStorage`
      // calls `engine.stop()`, which drops `isStarting` and would otherwise let a
      // pending retry re-open the DB while it's being deleted.
      resettingRef.current = true;
      // Breadcrumb survives the reload so the next boot knows a wipe was already
      // attempted (stops the escalation ladder from looping).
      markBootRecovery(reason);
      engine.track("boot.recovery.reset", { auto: reason === "auto-reset" });
      void hardResetOfflineStorage().finally(() => {
        window.location.reload();
      });
    },
    [engine],
  );

  useEffect(() => {
    if (!active) {
      // Boot finished (or splash torn down). If this cycle had stalled, or it
      // followed a recent recovery action, the boot ultimately RECOVERED —
      // report it (once) and clear the breadcrumb so a later fast boot doesn't
      // re-attribute it. A plain fast boot (no stall, no recent breadcrumb)
      // emits nothing.
      if (activeSinceRef.current !== null) {
        const prior = priorRecoveryRef.current;
        const recentPrior =
          prior && Date.now() - prior.at < RECOVERY_RECENT_MS ? prior : null;
        if (stalledThisCycleRef.current || recentPrior) {
          engine.track("boot.recovered", {
            afterMs: Date.now() - activeSinceRef.current,
            wasStalled: stalledThisCycleRef.current,
            priorReason: recentPrior?.reason ?? null,
          });
        }
        if (recentPrior) clearBootRecovery();
        priorRecoveryRef.current = null;
      }
      // Clear the stalled state so a later in-place reset/re-mount starts from a
      // clean slate.
      setStalled(false);
      stalledThisCycleRef.current = false;
      softRetriedRef.current = false;
      activeSinceRef.current = null;
      return;
    }

    if (activeSinceRef.current === null) activeSinceRef.current = Date.now();
    const elapsed = Date.now() - activeSinceRef.current;
    const remaining = (deadline: number) => Math.max(0, deadline - elapsed);

    // Soft-retry, self-rescheduling: at the deadline we re-kick the engine
    // ONCE, but only when no start is in flight — re-kicking a running start
    // would spawn an overlapping cold-start wave. If a start IS running (a
    // hung open(), or a slow-but-live cold start), we don't burn the one-shot
    // slot: we re-check shortly, so a start that later bails without becoming
    // ready still gets its single retry. The stall timer bounds the wait
    // regardless.
    let softTimer: ReturnType<typeof setTimeout>;
    const trySoftRetry = () => {
      if (softRetriedRef.current || resettingRef.current) return;
      if (engine.isStarting) {
        softTimer = setTimeout(trySoftRetry, SOFT_RETRY_RECHECK_MS);
        return;
      }
      softRetriedRef.current = true;
      engine.track("boot.soft_retry", { blocked });
      // Fire-and-forget; never throws out of here.
      void engine.start().catch(() => {});
    };
    softTimer = setTimeout(trySoftRetry, remaining(SOFT_RETRY_MS));

    // `blocked` only ever shortens the fuse (BLOCKED_STALL_MS < STALL_MS), so
    // capping against elapsed guarantees the panel never appears later than the
    // plain STALL_MS path would have delivered it.
    const stallTimer = setTimeout(
      () => {
        if (resettingRef.current) return;
        // Only report the stall ONCE per active cycle. A late `blocked` flip
        // re-runs this effect and re-arms the timer with a now-elapsed
        // (remaining=0) deadline, which would otherwise fire a duplicate
        // `boot.stalled` + Sentry alert for the same stuck boot.
        if (stalledThisCycleRef.current) return;
        stalledThisCycleRef.current = true;

        const prior = priorRecoveryRef.current;
        const escalate = Boolean(
          prior &&
            Date.now() - prior.at < RECOVERY_RECENT_MS &&
            ESCALATE_FROM.has(prior.reason),
        );

        engine.track("boot.stalled", {
          blocked,
          escalate,
          priorReason: prior?.reason ?? null,
          elapsedMs: Date.now() - (activeSinceRef.current ?? Date.now()),
        });
        // Sentry breadcrumb → alertable. A stuck boot used to be invisible; this
        // is the signal on-call watches. Network-only stalls are already noisy,
        // so tag the cause for triage rather than dropping them.
        Sentry.captureMessage("boot stalled", {
          level: "warning",
          tags: {
            boot_blocked: String(blocked),
            boot_escalate: String(escalate),
            boot_prior_reason: prior?.reason ?? "none",
          },
        });

        setStalled(true);
        if (escalate) {
          // A previous reload didn't clear the wedge — don't ask the user to
          // reload into the same wall. Wipe local data automatically (keeps them
          // signed in) and reload. Runs at most once per window: the "auto-reset"
          // breadcrumb it drops is excluded from ESCALATE_FROM.
          setAutoRecovering(true);
          engine.track("boot.escalated_auto_reset", { blocked });
          runReset("auto-reset");
        }
      },
      remaining(blocked ? BLOCKED_STALL_MS : STALL_MS),
    );

    return () => {
      clearTimeout(softTimer);
      clearTimeout(stallTimer);
    };
  }, [active, blocked, engine, runReset]);

  const restart = useCallback(() => {
    markBootRecovery("reload");
    engine.track("boot.recovery.reload", { blocked });
    window.location.reload();
  }, [engine, blocked]);

  return {
    stalled,
    blocked,
    autoRecovering,
    restart,
  };
}
